// src/pages/AyahVerificationPage.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    Button,
    Textarea,
    Flex,
    Spinner,
    useToast,
    Badge,
    VStack,
    HStack,
    Divider,
    Card,
    CardBody,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Tooltip,
    IconButton,
    Tabs, TabList, TabPanels, Tab, TabPanel
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, SearchIcon, DownloadIcon, TriangleDownIcon, RepeatClockIcon, WarningTwoIcon } from '@chakra-ui/icons';
import Sidebar from '../../components/Dashboard/Sidebar';
import { fetchAllSurahs, fetchSurah, getSignedUrl, downloadFile, fetchAudioRecitations } from '../../api/quranService';
import { fetchTranslationInfo, fetchTranslation } from '../../api/translationVerificationService';
import { fetchAudio, fetchAudioInfo, verifyAudio, deleteVerification } from '../../api/audioVerificationService';

const RECITERS = { //Enum for audio
    "1": "Alafasy",
    "2": "Abdul Baseet"
};

// Individual Ayah Card Component
const AyahCard = ({ ayah, selectedSurahId, selectedReciterId, onVerify, onDelete, onPlayAudio, onDownload, verifying, loading }) => {
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleVerify = async (status) => {
        setIsSubmitting(true);
        try {
            await onVerify(ayah.ayah_key, status, comment);
            setComment(''); // Clear comment after successful verification
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setIsSubmitting(true);
        try {
            await onDelete(ayah.ayah_key);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isCurrentlyVerifying = verifying === ayah.ayah_key || isSubmitting;

    return (
        <Card border="1px" borderColor="gray.200">
            <CardBody>
                <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                        <Text fontWeight="bold" fontSize="lg">Ayah {ayah.ayah_index}</Text>
                        {ayah.user_verification ? (
                            ayah.user_verification.comments ?
                                <Badge colorScheme="yellow">Verified with Correction</Badge> :
                                <Badge colorScheme="green">Verified</Badge>
                        ) : (
                            <Badge colorScheme="yellow">Pending</Badge>
                        )}
                    </HStack>

                    <Box flex="1" mb={{ base: 3, md: 0 }}>
                        <Text fontSize="2xl" textAlign="right" mb={2} dir="rtl" fontFamily="QuranFont">
                            {ayah.recitation?.text}
                        </Text>
                        <Text fontSize="md">
                            {ayah.translations?.text || ""}
                        </Text>
                    </Box>

                    <Divider />

                    <Box flex="1" mb={{ base: 3, md: 0 }} ml="auto">
                        <Tooltip label="Play">
                            <IconButton
                                size="sm"
                                aria-label="Play verse"
                                icon={<TriangleDownIcon transform="rotate(-90deg)" />}
                                colorScheme="blue"
                                mr={2}
                                onClick={() => onPlayAudio("audio/" + ayah.audio_url)}
                                isDisabled={loading}
                            />
                        </Tooltip>
                        <Tooltip label="Download">
                            <IconButton
                                size="sm"
                                aria-label="Download verse"
                                icon={<DownloadIcon />}
                                colorScheme="blue"
                                onClick={() => onDownload(
                                    "audio/" + ayah.audio_url,
                                    `Surah_${selectedSurahId}_Verse_${ayah.ayah_index}_${RECITERS[selectedReciterId]}.mp3`
                                )}
                                isDisabled={loading}
                            />
                        </Tooltip>
                    </Box>

                    {!ayah.user_verification && (
                        <HStack justifyContent="flex-end" spacing={4} w="100%">
                            <Button
                                colorScheme="green"
                                leftIcon={<CheckIcon />}
                                onClick={() => handleVerify(true)}
                                isLoading={isCurrentlyVerifying}
                            >
                                Verify
                            </Button>
                        </HStack>
                    )}

                    <Divider />

                    {!ayah.user_verification ? (
                        // Pending verification form
                        <>
                            <Box>
                                <Text mb={2}>Expert Comment (optional):</Text>
                                <Textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Write your thoughts or corrections..."
                                    isDisabled={isCurrentlyVerifying}
                                />
                            </Box>
                            <HStack justifyContent="flex-end" spacing={4} w="100%">
                                <Button
                                    colorScheme="yellow"
                                    leftIcon={<WarningTwoIcon />}
                                    onClick={() => handleVerify(false)}
                                    isLoading={isCurrentlyVerifying}
                                    disabled={!comment}
                                >
                                    Verify with Correction
                                </Button>
                            </HStack>
                        </>
                    ) : (
                        // Already verified - show revoke option
                        <HStack justifyContent="flex-end" spacing={4} w="100%">
                            <Button
                                colorScheme="blue"
                                leftIcon={<RepeatClockIcon />}
                                onClick={handleDelete}
                                isLoading={isCurrentlyVerifying}
                            >
                                Revoke
                            </Button>
                        </HStack>
                    )}
                </VStack>
            </CardBody>
        </Card>
    );
};

const AudioVerificationPage = () => {
    const toast = useToast();

    const [surahs, setSurahs] = useState([]);
    const [selectedSurahId, setSelectedSurahId] = useState('');
    const [filteredSurahs, setFilteredSurahs] = useState([]);
    const [reciterInfos, setReciterInfos] = useState([]);
    const [selectedReciterId, setSelectedReciterId] = useState('1');
    const [searchTerm, setSearchTerm] = useState('');

    const [ayahs, setAyahs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const [currentAudio, setCurrentAudio] = useState(null);
    const [audioLoading, setAudioLoading] = useState(false);
    const audioRef = React.useRef(null);

    const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);

    // Load all surahs once
    useEffect(() => {
        const loadSurahs = async () => {
            setLoading(true);
            try {
                const [surahsData, translationData] = await Promise.all([
                    fetchAllSurahs(),
                    fetchAudioInfo(),
                ]);

                if (surahsData) {
                    setSurahs(surahsData?.surahs);
                    setFilteredSurahs(surahsData?.surahs);
                }

                if (translationData) {
                    setReciterInfos(translationData);
                }
            } catch (err) {
                toast({
                    title: 'Failed to load surahs',
                    description: err.message || 'Something went wrong',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        loadSurahs();
    }, []);

    // Fetch ayahs on surah selection
    useEffect(() => {
        if (!selectedSurahId) return;
        const fetchSurahAyahs = async () => {
            setLoading(true);
            try {
                const data = await fetchAudio(selectedSurahId, selectedReciterId);
                setAyahs(data.ayahs);
            } catch (err) {
                toast({
                    title: 'Error fetching ayahs',
                    description: err.message,
                    status: 'error',
                    duration: 4000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };
        fetchSurahAyahs();
    }, [selectedSurahId, selectedReciterId]);

    const handleVerify = async (ayahKey, status, comment) => {
        setVerifying(ayahKey);
        try {
            await verifyAudio(ayahKey, selectedReciterId, status, comment);
            toast({
                title: 'Verification submitted',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            const updated = await fetchAudio(selectedSurahId, selectedReciterId);
            setAyahs(updated.ayahs);
        } catch (err) {
            toast({
                title: 'Verification failed',
                description: err.message,
                status: 'error',
                duration: 4000,
                isClosable: true
            });
            throw err; // Re-throw to let the individual component handle it
        } finally {
            setVerifying(null);
        }
    };

    const handleDelete = async (ayahKey) => {
        setVerifying(ayahKey);
        try {
            await deleteVerification(ayahKey, selectedReciterId);
            toast({
                title: 'Verification deleted',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            const updated = await fetchAudio(selectedSurahId, selectedReciterId);
            setAyahs(updated.ayahs);
        } catch (err) {
            toast({
                title: 'Delete failed',
                description: err.message,
                status: 'error',
                duration: 4000,
                isClosable: true
            });
            throw err; // Re-throw to let the individual component handle it
        } finally {
            setVerifying(null);
        }
    };

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        const filtered = surahs.filter(s =>
            s.ename.toLowerCase().includes(term.toLowerCase()) ||
            s.tname.toLowerCase().includes(term.toLowerCase()) ||
            s._id.toString().includes(term)
        );
        setFilteredSurahs(filtered);
    };

    const handlePlayAudio = async (url) => {
        try {
            setAudioLoading(true);
            const signedUrl = await getSignedUrl(url);
            setCurrentAudio(signedUrl);

            setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.play().catch(error => {
                        console.error("Audio playback failed:", error);
                        toast({
                            title: "Playback failed",
                            description: "Unable to play the audio file",
                            status: "error",
                            duration: 3000,
                            isClosable: true,
                        });
                    });
                }
            }, 100);
        } catch (err) {
            console.error('Failed to play audio:', err);
            toast({
                title: "Error",
                description: "Failed to play audio",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setAudioLoading(false);
        }
    };

    const handleDownload = async (url, fileName) => {
        try {
            setLoading(true);
            const key = extractKeyFromUrl(url);
            await downloadFile(key, fileName);

            toast({
                title: "Download started",
                description: `Downloading ${fileName || "audio file"}`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (err) {
            console.error('Download failed:', err);
            toast({
                title: "Download failed",
                description: "Unable to download the file",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const extractKeyFromUrl = (url) => {
        const matches = url.match(/amazonaws\.com\/(.+?)\?/);
        if (matches && matches[1]) {
            return matches[1];
        }
        return url;
    };

    // Split ayahs into pending and completed
    const pendingAyahs = ayahs.filter(ayah => !ayah.user_verification);
    const completedAyahs = ayahs.filter(ayah => ayah.user_verification);

    return (
        <Flex direction="row" h="100vh">
            <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
            <Box
                pl={sidebarCollapsed ? "60px" : "250px"}
                w="full"
                overflowY="auto"
                pb={8}
                transition="padding-left 0.2s ease"
            >
                <Box p={8}>
                    <Heading mb={6}>Audio Verification</Heading>

                    {/* Surah Selector */}
                    <Box maxW="lg" mb={8}>
                        <InputGroup mb={2}>
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.300" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search Surah by name or number"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </InputGroup>

                        <Select
                            placeholder={`${loading ? "Loading Surah Data ..." : "Select a Surah"}`}
                            onChange={(e) => setSelectedSurahId(e.target.value)}
                            value={selectedSurahId}
                        >
                            {filteredSurahs.map((surah) => (
                                <option key={surah._id} value={surah._id}>
                                    {surah._id}. {surah.tname} ({surah.ename})
                                </option>
                            ))}
                        </Select>
                        <Select
                            placeholder={`${loading ? "Loading Reciter Data ..." : "Select Reciter"}`}
                            onChange={(e) => setSelectedReciterId(e.target.value)}
                            value={selectedReciterId}
                        >
                            {reciterInfos.map((infos) => (
                                <option key={infos._id} value={infos._id}>
                                    {infos.reciter_name}
                                </option>
                            ))}
                        </Select>
                    </Box>

                    {/* Audio Player */}
                    <Box>
                        {currentAudio && (
                            <Box mb={6} p={4} borderWidth="1px" borderRadius="lg" bg="gray.50">
                                <Flex justify="space-between" align="center" flexDirection={{ base: 'column', md: 'row' }}>
                                    <Text mb={{ base: 2, md: 0 }}>
                                        {audioLoading ? "Loading audio..." : "Now Playing"}
                                    </Text>
                                    <audio ref={audioRef} src={currentAudio} controls />
                                </Flex>
                            </Box>
                        )}
                    </Box>

                    {loading ? (
                        <Flex justify="center" align="center" minH="200px">
                            <Spinner size="xl" />
                        </Flex>
                    ) : (
                        <>
                            {selectedSurahId && (
                                <Tabs variant="enclosed" colorScheme="teal">
                                    <TabList>
                                        <Tab>Pending ({pendingAyahs.length})</Tab>
                                        <Tab>Completed ({completedAyahs.length})</Tab>
                                    </TabList>

                                    <TabPanels>
                                        {/* Pending Tab */}
                                        <TabPanel>
                                            <VStack spacing={6} align="stretch">
                                                {pendingAyahs.map((ayah) => (
                                                    <AyahCard
                                                        key={ayah.ayah_key}
                                                        ayah={ayah}
                                                        selectedSurahId={selectedSurahId}
                                                        selectedReciterId={selectedReciterId}
                                                        onVerify={handleVerify}
                                                        onDelete={handleDelete}
                                                        onPlayAudio={handlePlayAudio}
                                                        onDownload={handleDownload}
                                                        verifying={verifying}
                                                        loading={loading}
                                                    />
                                                ))}
                                                {pendingAyahs.length === 0 && (
                                                    <Text textAlign="center" color="gray.500">
                                                        No pending ayahs for verification.
                                                    </Text>
                                                )}
                                            </VStack>
                                        </TabPanel>

                                        {/* Completed Tab */}
                                        <TabPanel>
                                            <VStack spacing={6} align="stretch">
                                                {completedAyahs.map((ayah) => (
                                                    <AyahCard
                                                        key={ayah.ayah_key}
                                                        ayah={ayah}
                                                        selectedSurahId={selectedSurahId}
                                                        selectedReciterId={selectedReciterId}
                                                        onVerify={handleVerify}
                                                        onDelete={handleDelete}
                                                        onPlayAudio={handlePlayAudio}
                                                        onDownload={handleDownload}
                                                        verifying={verifying}
                                                        loading={loading}
                                                    />
                                                ))}
                                                {completedAyahs.length === 0 && (
                                                    <Text textAlign="center" color="gray.500">
                                                        No completed verifications yet.
                                                    </Text>
                                                )}
                                            </VStack>
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Flex>
    );
};

export default AudioVerificationPage;